import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { formatMoney, formatMoneyRoundWithoutDecimals } from '../../../util/currency';
import { types as sdkTypes } from '../../../util/sdkLoader';

import { OrderPanel, ListingImageGallery, IconArrowHead } from '../../../components';
import { DisplayCard } from '../DisplayCard/DisplayCard';
import FullscreenImages from '../../TransactionPage/FullscreenImages/FullscreenImages';

import css from './RentalsSection.module.css';

const { Money } = sdkTypes;
const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const Swiper =
  typeof window !== 'undefined'
    ? require('swiper/react').Swiper
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });
const SwiperSlide =
  typeof window !== 'undefined'
    ? require('swiper/react').SwiperSlide
    : dynamic(() => import('swiper/react'), {
        ssr: false,
      });

const RentalsSection = props => {
  const {
    config,
    configLayout,
    rentals,
    variantPrefix,
    currentListing,
    rentalDisplayId,
    marketplaceCurrency,
    intl,
    isOwnListing,
    onSubmit,
    authorLink,
    title,
    titleDesktop,
    payoutDetailsWarning,
    author,
    onManageDisableScrolling,
    onContactUser,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    validListingTypes,
    dayCountAvailableForBooking,
    marketplaceName,
    activeRental,
    setActiveRental,
    brandProfileListingPendingApproval,
    hasBrandProfileListing,
    currentUser,
    displayTypeOptions,
    detailsSection,
    measureUnit,
    brand,
    onFetchMonthlyTimeSlotsForDisplayListing,
    activeId,
    isDe,
    setCurrentTx
  } = props;
  // const [hoveredListingId, setHoveredListingId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [listingChanged, setListingChanged] = useState(false);

  useEffect(() => {
    // Load swiper CSS only in the browser
    if (typeof window !== 'undefined') {
      require('swiper/css');
      require('swiper/css/bundle');
    }
  }, []);

  useEffect(() => {
    if (rentals && rentals.length > 0) {
      const defaultDisplay = rentals.find(r => r.id.uuid === rentalDisplayId?.uuid);
      const newActiveRental = defaultDisplay ? defaultDisplay : rentals[0];
      const activeListing =
        (activeId && rentals.find(r => r.id.uuid === activeId)) || newActiveRental;

      // if (!activeRental || activeRental.id.uuid !== newActiveRental.id.uuid) {
      setActiveRental(activeListing);
      onFetchMonthlyTimeSlotsForDisplayListing(activeListing);
      // }
    }
  }, [rentals, activeId]);

  const Navigation =
    typeof window !== 'undefined'
      ? require('swiper/modules').Navigation
      : dynamic(() => import('swiper/modules'), {
          ssr: false,
        });

  const Controller =
    typeof window !== 'undefined'
      ? require('swiper/modules').Controller
      : dynamic(() => import('swiper/modules'), {
          ssr: false,
        });

  const images = activeRental && activeRental?.images ? activeRental?.images : [];

  // const imageVariants = ['scaled-medium', 'scaled-large', 'scaled-xlarge'];
  const imageVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];
  const thumbnailVariants = [variantPrefix, `${variantPrefix}-2x`, `${variantPrefix}-4x`];
  const aditionalGalleryOptions = {
    showThumbnails: false,
  };
  const publicData = activeRental?.attributes?.publicData || {};
  const { finalDisplayRate, spaceDescription, displayImagesOrder } = publicData || [];

  const orderedImages =
    images &&
    [...images].sort((a, b) => {
      const aIndex = displayImagesOrder?.indexOf(a.id?.uuid);
      const bIndex = displayImagesOrder?.indexOf(b.id?.uuid);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  const rentalRichTitle = (
    <span>
      {richText(activeRental?.attributes?.title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const formattedFinalDisplayRate =
    finalDisplayRate && finalDisplayRate.amount
      ? formatMoneyRoundWithoutDecimals(
          intl,
          new Money(finalDisplayRate.amount, marketplaceCurrency)
        ) + ' day'
      : '';

  const swiperTopRef = useRef(null);
  const swiperBottomRef = useRef(null);
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    if (!swiperTopRef.current) return;

    const swiper = swiperTopRef.current;

    const checkNavVisibility = () => {
      const slidesCount = swiper.slides.length;
      const currentSlidesPerView =
        typeof swiper.params.slidesPerView === 'number'
          ? swiper.params.slidesPerView
          : swiper.params.breakpoints &&
            Object.entries(swiper.params.breakpoints)
              .sort((a, b) => +a[0] - +b[0])
              .reduce((acc, [breakpoint, config]) => {
                if (window.innerWidth >= +breakpoint && config.slidesPerView) {
                  acc = config.slidesPerView;
                }
                return acc;
              }, swiper.params.slidesPerView);

      setShowNav(slidesCount > currentSlidesPerView);
    };

    checkNavVisibility();
    window.addEventListener('resize', checkNavVisibility);

    return () => window.removeEventListener('resize', checkNavVisibility);
  }, [rentals]);

  useEffect(() => {
    if (
      swiperTopRef.current &&
      swiperBottomRef.current &&
      swiperTopRef.current.controller &&
      swiperBottomRef.current.controller
    ) {
      swiperTopRef.current.controller.control = swiperBottomRef.current;
      swiperBottomRef.current.controller.control = swiperTopRef.current;
    }
  }, []);

  const formattedImages = orderedImages?.map(image => {
    const originalImage = image?.attributes?.variants?.['display-card-4x'];
    const thumbnailImage = image?.attributes?.variants?.['display-card'];

    return {
      original: originalImage?.url,
      thumbnail: thumbnailImage?.url,
      name: originalImage?.name,
    };
  });

  if (!rentals?.length || !window || typeof window === 'undefined') {
    return null;
  }

  return (
    <>
      <div className={css.swiperWrapper}>
        <div className={css.displaysContainer}>
          <button
            className={classNames(css.navLeft, { [css.hidden]: !showNav })}
            disabled={!showNav}
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="left" size="big" />
            </div>
          </button>

          <button
            className={classNames(css.navRight, { [css.hidden]: !showNav })}
            disabled={!showNav}
          >
            <div className={css.navArrowWrapper}>
              <IconArrowHead direction="right" size="big" />
            </div>
          </button>

          <Swiper
            onSwiper={swiperInstance => {
              swiperTopRef.current = swiperInstance;
            }}
            className={css.swiperForListings}
            // loop={true}
            spaceBetween={20}
            // slidesPerView={5}
            navigation={{
              nextEl: `.${css.navRight}`,
              prevEl: `.${css.navLeft}`,
            }}
            modules={[Navigation, Controller]}
            breakpoints={{
              1200: {
                slidesPerView: 5,
              },
              992: {
                slidesPerView: 5,
              },
              768: {
                slidesPerView: 4,
              },
              576: {
                slidesPerView: 3,
              },
              0: {
                slidesPerView: 2,
              },
            }}
          >
            {rentals.map((l, i) => {
              // const isHovered = hoveredListingId === l.id.uuid;
              const panelWidth = 62.5;

              const renderSizes = [
                `(max-width: 767px) 100vw`,
                `(max-width: 1920px) ${panelWidth / 2}vw`,
                `${panelWidth / 3}vw`,
              ].join(', ');

              const handleClick = async l => {
                setActiveRental(l);

                if (l && activeRental && activeRental?.id?.uuid !== l?.id.uuid) {
                  setListingChanged(prev => !prev);
                  await onFetchMonthlyTimeSlotsForDisplayListing(l, true);
                }
              };

              return (
                <SwiperSlide
                  key={`${l.id.uuid}-top-${i}`}
                  className={classNames(css.slide, css.slideImage, {
                    [css.slideImageActive]: activeRental?.id?.uuid === l.id.uuid,
                    // [css.slideImageHovered]: isHovered,
                  })}
                  // onMouseEnter={() => setHoveredListingId(l.id.uuid)}
                  // onMouseLeave={() => setHoveredListingId(null)}
                >
                  <DisplayCard
                    setActiveListing={handleClick}
                    activeListing={activeRental}
                    listing={l}
                    layout={configLayout}
                    renderSizes={renderSizes}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
        <Swiper
          onSwiper={swiperInstance => {
            swiperBottomRef.current = swiperInstance;
          }}
          className={css.swiperForListings}
          // loop={true}
          spaceBetween={20}
          // slidesPerView={5}
          navigation={{
            nextEl: `.${css.navRight}`,
            prevEl: `.${css.navLeft}`,
          }}
          // modules={[Navigation]}
          modules={[Controller]}
          breakpoints={{
            1200: {
              slidesPerView: 5,
            },
            992: {
              slidesPerView: 5,
            },
            768: {
              slidesPerView: 4,
            },
            576: {
              slidesPerView: 3,
            },
            0: {
              slidesPerView: 2,
            },
          }}
        >
          {rentals.map((l, i) => {
            const { title = '', price, publicData } = l.attributes;
            const { displayType, displayLength } = publicData || {};
            const formattedPrice = price
              ? formatMoneyRoundWithoutDecimals(intl, new Money(price?.amount, config?.currency))
              : null;

            const handleClick = async () => {
              setActiveRental(l);

              if (l && activeRental && activeRental?.id?.uuid !== l?.id.uuid) {
                setListingChanged(prev => !prev);
                await onFetchMonthlyTimeSlotsForDisplayListing(l, true);
              }
            };

            // const isHovered = hoveredListingId === l.id.uuid;

            return (
              <SwiperSlide
                key={`${l.id.uuid}-bottom-${i}`}
                className={classNames(css.slide, css.slideText, {
                  [css.slideTextActive]: activeRental?.id?.uuid === l.id.uuid,
                })}
                // onMouseEnter={() => setHoveredListingId(l.id.uuid)}
                // onMouseLeave={() => setHoveredListingId(null)}
              >
                <div className={css.info} onClick={handleClick}>
                  <span style={{ display: 'block' }}>{title}</span>
                  <span style={{ display: 'block' }}>
                    {l?.attributes?.publicData?.displayType?.map((t, index) => {
                      return (
                        <React.Fragment key={index}>
                          <span className={css.item}>{isDe ? displayTypeOptions?.[t]?.labelDe : displayTypeOptions?.[t]?.label}</span>
                          {index < displayType?.length - 1 && ', '}
                        </React.Fragment>
                      )
                    })}
                  </span>
                  <span style={{ display: 'block' }}>{displayLength + measureUnit}</span>
                  {formattedPrice && (
                    <FormattedMessage
                      id="ListingCard.priceToDisplay"
                      values={{ price: formattedPrice }}
                    />
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
      <div className={css.root}>
        <div className={css.rentalGallery} data-testid="carousel">
          <ListingImageGallery
            images={orderedImages}
            imageVariants={imageVariants}
            thumbnailVariants={thumbnailVariants}
            aspectWidth={1}
            aspectHeight={1}
            thumbnailClassName={css.thumb}
            aditionalGalleryOptions={aditionalGalleryOptions}
            rootClassName={css.listingDisplayGalleryRoot}
            isDisplay
            customOnScreenChange={() => setIsFullscreen(true)}
          />

          <FullscreenImages
            id="DisplayImagesId"
            images={formattedImages}
            alreadyFormatted
            disableDownload
            isModalOpen={isFullscreen}
            onClose={() => setIsFullscreen(false)}
            onManageDisableScrolling={onManageDisableScrolling}
            thumbnailClassName={css.thumb}
            rootGalleryClassName={css.rootGallery}
            wrapperFullscreen={css.wrapperFullscreen}
          />

          <div style={{ marginTop: 24 }}>
            <label className={css.mainLabel}>
              <FormattedMessage id="RentalsSection.description" />
            </label>
            <p className={css.content}>{spaceDescription}</p>
            {/*<Heading*/}
            {/*  as="h2"*/}
            {/*  rootClassName={css.sectionHeading}*/}
            {/*  style={{ marginTop: 21, marginBottom: 0 }}*/}
            {/*>*/}
            {/*  {rentalRichTitle}*/}
            {/*</Heading>*/}
            {/*{displayType &&*/}
            {/*  displayType*/}
            {/*    .map(displayItem =>*/}
            {/*      intl.formatMessage({ id: `RentalsSection.displayType.${displayItem}` })*/}
            {/*    )*/}
            {/*    .join(', ')}*/}
            {/*<br />*/}
            {/*{displayLength && measureUnit && displayLength + measureUnit}*/}
            {/*<br />*/}
            {/*<strong>{formattedFinalDisplayRate}</strong>*/}
            {/*{spaceDescription && <p>{spaceDescription}</p>}*/}
          </div>
          {detailsSection}
        </div>
        <div className={css.container}>
          {/* <Heading as="h2" rootClassName={css.sectionHeading}>
                    <FormattedMessage id="RentalsSection.title" />
                    </Heading> */}

          <OrderPanel
            key={listingChanged}
            className={css.productOrderPanel}
            rootClassName={css.orderPanelRoot}
            listing={activeRental}
            isOwnListing={isOwnListing}
            onSubmit={onSubmit}
            authorLink={authorLink}
            title={title}
            titleDesktop={titleDesktop}
            payoutDetailsWarning={payoutDetailsWarning}
            author={author}
            onManageDisableScrolling={onManageDisableScrolling}
            onContactUser={onContactUser}
            monthlyTimeSlots={monthlyTimeSlots}
            onFetchTimeSlots={onFetchTimeSlots}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            validListingTypes={validListingTypes}
            marketplaceCurrency={marketplaceCurrency}
            dayCountAvailableForBooking={dayCountAvailableForBooking}
            marketplaceName={marketplaceName}
            brandProfileListingPendingApproval={brandProfileListingPendingApproval}
            hasBrandProfileListing={hasBrandProfileListing}
            currentUser={currentUser}
            brand={brand}
            setCurrentTx={setCurrentTx}
          />
        </div>
      </div>
    </>
  );
};

export default RentalsSection;
